import type { OrderItem_V1 } from "@@/shared/orders/order-items/order-item-v1";
import type { OrderItem_V2 } from "@@/shared/orders/order-items/order-item-v2";
import { unique } from "@@/utilities/unique";
import { z } from "zod";
import type { DiscountCondition } from "./discount-condition";
import type {
    DiscountConditionId} from "./discount-condition-id";
import {
    createDiscountConditionId
} from "./discount-condition-id";
import type { DiscountConditionValidator } from "./discount-condition-validator";

const itemsConditionType = "ITEMS_CONDITION";
type ItemsConditionType = typeof itemsConditionType;

type ItemsCondition = {
    _id: DiscountConditionId;
    type: ItemsConditionType;
    itemIds: string[];
};

const createItemsCondition = (ids: string[]): ItemsCondition => {
    return {
        _id: createDiscountConditionId(),
        type: "ITEMS_CONDITION",
        itemIds: unique(ids),
    };
};

const getItemsConditionValidator =
    <TOrderItem extends OrderItem_V1 | OrderItem_V2 = OrderItem_V2>(
        condition: ItemsCondition,
    ): DiscountConditionValidator<TOrderItem> =>
    (items: TOrderItem[]) => {
        const output = items.filter(item =>
            condition.itemIds.includes(item._id),
        );
        return output;
    };

const itemsConditionZodSchema: z.ZodSchema<ItemsCondition> = z.object({
    _id: z.string() as unknown as z.ZodSchema<DiscountConditionId>,
    type: z.literal("ITEMS_CONDITION"),
    itemIds: z.array(z.string()),
});

const isItemsCondition = (
    condition: DiscountCondition,
): condition is ItemsCondition => {
    return condition.type === itemsConditionType;
};

export {
    createItemsCondition,
    getItemsConditionValidator,
    isItemsCondition,
    itemsConditionType,
    itemsConditionZodSchema,
};
export type { ItemsCondition, ItemsConditionType };
